/**
 * Equipment Matcher Service
 *
 * Business logic for deriving wizard selections from rig and rock drill data.
 * Pure functions with no React dependencies — they work identically in web
 * (PWA) and React Native environments.
 *
 * PORTED VERBATIM from TH ToolFinder v4
 *   `tophammer-toolfinder-v4/src/services/equipmentMatcher.js` (114 lines).
 *
 * Source-of-truth reference:
 *   TopHammer_ToolFinder_Phase3_7_Integration_Guide.md
 *
 * MRT divergence note — DTH follow-up
 *   The literal `drillingType: 'top_hammer'` in both `deriveFromRig` and
 *   `deriveFromRockDrill` reflects TF v4's TH-only scope. MRT's Replace
 *   path supports TH AND DTH (when rigs.json includes DTH-capable rigs).
 *   For now the literal is preserved because the rigs/rockDrills data
 *   files copied across from TF v4 are TH-only. When DTH rigs land,
 *   these functions need a branch on the rig/drill product line — flagged
 *   as a known follow-up in the SP3.A.2 brief.
 *
 * @module services/equipmentMatcher
 * @since 2026-05-18 (Group D Sub-Plan 3, Task SP3.A.2)
 */

/**
 * Derive wizard selections from a rig selection.
 * Maps rig properties to the wizard's selection model.
 *
 * @param {Object} rig - Rig object from rigs.json
 * @returns {Object} Derived selections (application, threadSystem, equipmentClass, etc.)
 */
export function deriveFromRig(rig) {
  if (!rig) return {};

  const derived = {
    application: rig.applications?.[0]?.toLowerCase() || 'drilling',
    drillingType: 'top_hammer',
    holeDiameterMin: rig.holeDiameter?.minMm || null,
    holeDiameterMax: rig.holeDiameter?.maxMm || null,
  };

  // Determine thread system.
  if (rig.threadSystem?.requiresUserChoice) {
    derived.threadSystem = null;
    derived.availableThreadSystems = rig.threadSystem.alternates || [];
    derived.requiresThreadChoice = true;
  } else {
    derived.threadSystem = rig.threadSystem?.primary || null;
    derived.requiresThreadChoice = false;
  }

  // Determine equipment class based on rock drill power.
  const power = rig.rockDrill?.primaryPowerKw || 0;
  if (power >= 40) derived.equipmentClass = 'extra_heavy';
  else if (power >= 25) derived.equipmentClass = 'heavy';
  else if (power >= 18) derived.equipmentClass = 'medium';
  else derived.equipmentClass = 'light';

  return derived;
}

/**
 * Derive wizard selections from a rock drill selection.
 * Maps rock drill properties to the wizard's selection model.
 *
 * @param {Object} drill - Rock drill object from rockDrills.json
 * @returns {Object} Derived selections (threadSystem, equipmentClass, etc.)
 */
export function deriveFromRockDrill(drill) {
  if (!drill) return {};

  const derived = {
    drillingType: 'top_hammer',
    threadSystem: drill.threadSystem || null,
    holeDiameterMin: drill.holeDiameterMin || null,
    holeDiameterMax: drill.holeDiameterMax || null,
    requiresThreadChoice: false,
  };

  // If drill has alternate threads, note them.
  if (drill.alternateThreads && drill.alternateThreads.length > 0) {
    derived.availableThreadSystems = [drill.threadSystem, ...drill.alternateThreads];
  }

  // Determine equipment class from power.
  const power = drill.powerKw || 0;
  if (power >= 40) derived.equipmentClass = 'extra_heavy';
  else if (power >= 25) derived.equipmentClass = 'heavy';
  else if (power >= 18) derived.equipmentClass = 'medium';
  else derived.equipmentClass = 'light';

  return derived;
}

/**
 * Get the equipment class label for display.
 *
 * @param {string} classId - Equipment class ID (light, medium, heavy, extra_heavy)
 * @returns {string} Human-readable label (unknown id is echoed back unchanged)
 */
export function getEquipmentClassLabel(classId) {
  const labels = {
    light: 'Light',
    medium: 'Medium',
    heavy: 'Heavy',
    extra_heavy: 'Extra Heavy',
  };
  return labels[classId] || classId;
}

/**
 * Get the thread system for a given equipment class.
 * Used in the Explore path (Path A) where class determines thread.
 *
 * @param {string} classId - Equipment class ID
 * @returns {string|null} Thread system (e.g., 'T38', 'T45', 'T51', 'GT60')
 */
export function getThreadForEquipmentClass(classId) {
  const threadMap = {
    light: 'T38',
    medium: 'T45',
    heavy: 'T51',
    extra_heavy: 'GT60',
  };
  return threadMap[classId] || null;
}
